import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createSupportTicket } from "../../services/supportService";
import { Field, inputClass } from "../../components/forms/Field";
import BackLink from "../../components/navigation/BackLink/BackLink";
import { AttachButton, AttachmentChips } from "../../components/support/AttachmentPicker";
import {
  SUPPORT_BODY_MAX,
  SUPPORT_CATEGORIES,
  SUPPORT_SUBJECT_MAX,
} from "../../utils/constants/support";
import { supportErrorMessage } from "../../utils/helpers/supportErrors";
import { readSupportOrigin } from "../../utils/helpers/supportOrigin";
import { guardUnsavedWork } from "../../utils/helpers/unsavedWork";

// Opening a ticket. The page the user came from rides along as a removable
// chip, so support sees where the problem is without asking.
export default function SupportNew() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [category, setCategory] = useState("question");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [pageUrl, setPageUrl] = useState(readSupportOrigin);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const dirty = !sending && (subject.trim() || body.trim() || files.length > 0);
  useEffect(() => guardUnsavedWork(dirty), [dirty]);

  const submit = async (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!subject.trim()) nextErrors.subject = t("support.required");
    if (!body.trim()) nextErrors.body = t("support.required");
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSending(true);
    try {
      const ticket = await createSupportTicket({
        body: {
          category,
          subject: subject.trim(),
          body: body.trim(),
          page_url: pageUrl,
          files,
        },
      });
      toast.success(t("support.created", { number: ticket.number }));
      navigate(`/support/${ticket.uuid}`, { replace: true });
    } catch (err) {
      setSending(false);
      toast.error(supportErrorMessage(err, t, t("support.createFailed")));
    }
  };

  return (
    <div className="flex-1 bg-slate-50 p-6">
      <div className="max-w-3xl mx-auto space-y-5">
        <BackLink to="/support" labelKey="support.backToList" />
        <form
          onSubmit={submit}
          noValidate
          className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 space-y-5"
        >
          <p className="text-sm text-slate-500 font-medium">{t("support.newSubtitle")}</p>

          <Field label={t("support.categoryLabel")}>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className={inputClass(false)}
              disabled={sending}
            >
              {SUPPORT_CATEGORIES.map((code) => (
                <option key={code} value={code}>
                  {t(`support.category.${code}`)}
                </option>
              ))}
            </select>
          </Field>

          <Field label={t("support.subjectLabel")} required error={errors.subject}>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              maxLength={SUPPORT_SUBJECT_MAX}
              placeholder={t("support.subjectPlaceholder")}
              className={inputClass(errors.subject)}
              disabled={sending}
            />
          </Field>

          <Field label={t("support.bodyLabel")} required error={errors.body}>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={7}
              maxLength={SUPPORT_BODY_MAX}
              placeholder={t("support.bodyPlaceholder")}
              className={`${inputClass(errors.body)} resize-y`}
              disabled={sending}
            />
          </Field>

          <div className="space-y-2">
            <AttachmentChips
              files={files}
              disabled={sending}
              onRemove={(index) => setFiles((current) => current.filter((_, i) => i !== index))}
            />
            <div className="flex flex-wrap items-center gap-2">
              <AttachButton files={files} onChange={setFiles} disabled={sending} />
              <span className="text-[11px] text-slate-500 font-medium">{t("support.attachHint")}</span>
            </div>
          </div>

          {pageUrl && (
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <span className="font-semibold">{t("support.pageLabel")}</span>
              <span className="inline-flex items-center gap-1 max-w-full rounded-lg bg-slate-100 border border-slate-200 pl-2.5 pr-1 py-1 font-mono">
                <span className="truncate">{pageUrl}</span>
                <button
                  type="button"
                  onClick={() => setPageUrl("")}
                  aria-label={t("support.removePage")}
                  className="w-5 h-5 rounded flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-200"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            </div>
          )}

          <div className="flex justify-end pt-1">
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold shadow-sm hover:bg-indigo-700 disabled:opacity-60 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2"
            >
              {sending ? t("support.sending") : t("support.submit")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
